<template>
  <!-- PC端：原有样式 -->
  <FilterSection
    v-if="!isMobile"
    :show-more="showMoreButton"
    :is-more-expanded="isMoreExpanded"
    @search="handleSearch"
    @reset="handleReset"
    @toggle-more="handleToggleMore"
    @advanced-search="openAdvancedSearch"
  >
    <FilterGroup
      v-for="filter in sortedFilters"
      :key="filter.label_id"
      :label="filter.label_name"
    >
      <!-- 输入框类型 -->
      <a-input
        v-if="filter.search_type === 'input'"
        :value="filterValues[filter.field_name]"
        :placeholder="`请输入${filter.label_name}`"
        class="filter-input"
        @input="handleInputChange(filter.field_name, $event.target.value)"
      />

      <!-- 选项类型（TagFilter，支持多选） -->
      <TagFilter
        v-else-if="filter.search_type === 'option'"
        v-model="filterValues[filter.field_name]"
        :options="filter.options"
        :show-more-count="getShowMoreCount(filter)"
        @update:modelValue="handleFilterChange(filter.field_name, $event)"
      />

      <!-- 单选类型（TagFilter，单选模式） -->
      <TagFilter
        v-else-if="filter.search_type === 'radio'"
        v-model="filterValues[filter.field_name]"
        :options="filter.options"
        :show-more-count="getShowMoreCount(filter)"
        :single="true"
        @update:modelValue="handleFilterChange(filter.field_name, $event)"
      />

      <!-- 树形类型（TreeFilter，支持多选） -->
      <TreeFilter
        v-else-if="filter.search_type === 'tree'"
        v-model="filterValues[filter.field_name]"
        :options="filter.options"
        @update:modelValue="handleFilterChange(filter.field_name, $event)"
      />
    </FilterGroup>
  </FilterSection>

  <!-- 移动端：下拉菜单形式 -->
  <MobileFilterContainer
    v-else
    ref="mobileFilterContainer"
    :filters="filters"
    :filter-values="filterValues"
    :label-group="labelGroup"
    @search="handleSearch"
    @reset="handleReset"
    @filter-change="handleFilterChange"
    @input-change="handleInputChange"
    @advanced-search="openAdvancedSearch"
  />

  <AdvancedSearchModal
    ref="advancedSearchModal"
    v-model:open="advancedSearchOpen"
    :label-group="labelGroup"
    @submit="handleAdvancedSubmit"
  />
</template>

<script>
import FilterSection from './FilterSection.vue'
import FilterGroup from './FilterGroup.vue'
import TagFilter from './TagFilter.vue'
import TreeFilter from './TreeFilter.vue'
import MobileFilterContainer from './MobileFilterContainer.vue'
import AdvancedSearchModal from './AdvancedSearchModal.vue'
import { getFilterOptions } from '@/api/filterApi'
import { isMobileDevice } from '@/utils/deviceDetection'
import { getPageSettings, getPublishedFilters, PAGE_CONFIG_UPDATED } from '@/services/pageConfigService'

export default {
  name: 'FilterContainer',
  components: {
    FilterSection,
    FilterGroup,
    TagFilter,
    TreeFilter,
    MobileFilterContainer,
    AdvancedSearchModal,
  },
  props: {
    labelGroup: {
      type: Number,
      required: true,
      validator: (value) => [1, 2, 3, 4, 5, 6].includes(value)
    }
  },
  emits: ['search', 'reset', 'advanced-search'],
  data() {
    return {
      filters: [],
      filterValues: {},
      isMoreExpanded: this.getDefaultExpanded(),
      collapseAfter: 4,
      isMobile: false,
      advancedSearchOpen: false,
    }
  },
  created() {
    this.checkMobile()
    this.applyPageSettings()
  },
  computed: {
    sortedFilters() {
      let sorted = [...this.filters]
      
      // 企业/市场端页面（labelGroup 为 1）需要特殊排序
      if (this.labelGroup === 1) {
        // 定义优先级顺序
        const priorityOrder = ['企业', '疾病领域', '设备类型', '时间范围']
        
        sorted = sorted.sort((a, b) => {
          const aIndex = priorityOrder.indexOf(a.label_name)
          const bIndex = priorityOrder.indexOf(b.label_name)
          
          // 如果都在优先级列表中，按优先级顺序排序
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex
          }
          // 如果只有 a 在优先级列表中，a 排在前面
          if (aIndex !== -1) {
            return -1
          }
          // 如果只有 b 在优先级列表中，b 排在前面
          if (bIndex !== -1) {
            return 1
          }
          // 如果都不在优先级列表中，按原来的 order 排序
          return a.order - b.order
        })
      } else {
        // 其他页面按原来的 order 排序
        sorted = sorted.sort((a, b) => a.order - b.order)
      }
      
      // 如果超过 N 个且已收起，只显示前 N 个
      if (sorted.length > this.collapseAfter && !this.isMoreExpanded) {
        return sorted.slice(0, this.collapseAfter)
      }
      return sorted
    },
    showMoreButton() {
      return this.filters.length > this.collapseAfter
    }
  },
  watch: {
    labelGroup: {
      immediate: true,
      handler(newVal, oldVal) {
        // 如果 labelGroup 改变（切换菜单），清除旧的状态和筛选条件
        if (oldVal && oldVal !== newVal) {
          this.clearFilterValues()
          // 重置筛选条件为默认值
          this.filterValues = {}
          // 切换页面不保留高级检索草稿
          this.advancedSearchOpen = false
          this.$refs.advancedSearchModal?.clearDraft?.()
          // 切换菜单时，关闭移动端筛选弹层
          if (this.$refs.mobileFilterContainer) {
            this.$refs.mobileFilterContainer.closeMenu()
            this.$refs.mobileFilterContainer.clearCommittedFilters()
          }
        }
        this.applyPageSettings()
        this.loadFilters()
      }
    }
  },
  mounted() {
    this.checkMobile()
    this.$nextTick(() => {
      this.checkMobile()
    })
    window.addEventListener('resize', this.checkMobile)
    this.onConfigUpdated = () => {
      this.applyPageSettings()
      this.loadFilters()
    }
    window.addEventListener(PAGE_CONFIG_UPDATED, this.onConfigUpdated)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener(PAGE_CONFIG_UPDATED, this.onConfigUpdated)
  },
  activated() {
    // 组件被激活时，检查是否有保存的状态
    // 如果有保存的状态，说明是从详情页返回，恢复筛选条件
    // 如果没有保存的状态，说明是切换菜单，不恢复
    const hasSavedState = this.hasSavedFilterValues()
    if (hasSavedState) {
      this.restoreFilterValues()
      // 恢复后立即清除保存的状态，避免一直保存
      this.$nextTick(() => {
        this.clearFilterValues()
      })
    }
  },
  methods: {
    getDefaultExpanded() {
      const settings = getPageSettings(this.labelGroup)
      if (settings) return settings.filterDefaultExpanded !== false
      return this.labelGroup !== 1
    },
    applyPageSettings() {
      const settings = getPageSettings(this.labelGroup)
      if (settings) {
        this.isMoreExpanded = settings.filterDefaultExpanded !== false
        this.collapseAfter = settings.filterCollapseAfter || 4
      } else {
        this.isMoreExpanded = this.labelGroup !== 1
        this.collapseAfter = 4
      }
    },
    mergePublishedFilters(apiFilters) {
      const published = getPublishedFilters(this.labelGroup)
      if (!published?.length) return apiFilters
      const apiMap = new Map(apiFilters.map((f) => [f.field_name, f]))
      return published.map((pf, index) => {
        const api = apiMap.get(pf.field_name)
        const options = pf.options || api?.options || []
        return {
          ...(api || {}),
          field_name: pf.field_name,
          label_name: pf.label_name,
          search_type: pf.search_type,
          options: Array.isArray(options) ? options : [],
          order: pf.sort ?? index + 1,
          label_id: api?.label_id ?? pf.id,
          enabled: true,
        }
      })
    },
    async loadFilters() {
      try {
        const response = await getFilterOptions(this.labelGroup)
        
        if (response.errno === 0) {
          let loaded = response.data || []
          loaded = this.mergePublishedFilters(loaded)
          this.filters = loaded
          
          // 初始化默认值（正常加载时不恢复，只在从详情页返回时恢复）
          // 如果没有保存的值，使用默认值
          if (Object.keys(this.filterValues).length === 0 || !this.filters.every(f => Object.prototype.hasOwnProperty.call(this.filterValues, f.field_name))) {
            const defaultValues = {}
            this.filters.forEach(filter => {
              // 如果已经有保存的值，使用保存的值
              if (this.filterValues[filter.field_name] !== undefined) {
                defaultValues[filter.field_name] = this.filterValues[filter.field_name]
              } else {
              // 如果是选项类型，默认值应该是数组（支持多选）
              if (filter.search_type === 'option') {
                const defaultValue = filter.default_value || ''
                // 如果默认值是"全部"或空字符串，转换为数组格式
                defaultValues[filter.field_name] = defaultValue ? [defaultValue] : []
              } else if (filter.search_type === 'radio') {
                // 单选类型，默认值应该是字符串或空字符串
                defaultValues[filter.field_name] = filter.default_value || ''
              } else if (filter.search_type === 'tree') {
                // 树形类型，默认值应该是数组（节点 ID 数组）
                defaultValues[filter.field_name] = Array.isArray(filter.default_value) 
                  ? filter.default_value 
                  : (filter.default_value ? [filter.default_value] : [])
              } else {
                // 输入框类型保持字符串
                defaultValues[filter.field_name] = filter.default_value || ''
              }
              }
            })
            this.filterValues = defaultValues
          }
        }
      } catch (error) {
        console.error('加载筛选选项失败:', error)
        // 如果 API 调用失败，可以回退到 mock 数据
        // const response = getFilterDataByGroup(this.labelGroup)
        // if (response.errno === 0) {
        //   this.filters = response.data
        //   const defaultValues = {}
        //   this.filters.forEach(filter => {
        //     defaultValues[filter.field_name] = filter.default_value
        //   })
        //   this.filterValues = defaultValues
        // }
      }
    },
    handleFilterChange(fieldName, value) {
      this.filterValues[fieldName] = value
    },
    handleInputChange(fieldName, value) {
      this.filterValues[fieldName] = value
    },
    handleSearch() {
      this.$refs.advancedSearchModal?.clearDraft?.()
      this.$emit('search', { ...this.filterValues })
    },
    handleReset() {
      // 重置为默认值
      const defaultValues = {}
      this.filters.forEach(item => {
        // 如果是选项类型，默认值应该是数组（支持多选）
        if (item.search_type === 'option') {
          const defaultValue = item.default_value || ''
          // 如果默认值是"全部"或空字符串，转换为数组格式
          defaultValues[item.field_name] = defaultValue ? [defaultValue] : []
        } else if (item.search_type === 'radio') {
          // 单选类型，默认值应该是字符串或空字符串
          defaultValues[item.field_name] = item.default_value || ''
        } else if (item.search_type === 'tree') {
          // 树形类型，默认值应该是数组（节点 ID 数组）
          defaultValues[item.field_name] = Array.isArray(item.default_value) 
            ? item.default_value 
            : (item.default_value ? [item.default_value] : [])
        } else {
          // 输入框类型保持字符串
          defaultValues[item.field_name] = item.default_value || ''
        }
      })
      this.filterValues = defaultValues
      this.$refs.advancedSearchModal?.clearDraft?.()
      this.$emit('reset', { ...this.filterValues })
    },
    openAdvancedSearch() {
      this.advancedSearchOpen = true
    },
    handleAdvancedSubmit(body) {
      this.$emit('advanced-search', body)
    },
    // 保存筛选条件到 sessionStorage（仅在跳转详情页时调用）
    saveFilterValues() {
      const stateKey = `filterContainer_state_${this.labelGroup}`
      try {
        sessionStorage.setItem(stateKey, JSON.stringify(this.filterValues))
      } catch (error) {
        console.error('保存筛选条件失败:', error)
      }
    },
    // 清除保存的筛选条件
    clearFilterValues() {
      const stateKey = `filterContainer_state_${this.labelGroup}`
      try {
        sessionStorage.removeItem(stateKey)
      } catch (error) {
        console.error('清除筛选条件失败:', error)
      }
    },
    // 检查是否有保存的筛选条件
    hasSavedFilterValues() {
      const stateKey = `filterContainer_state_${this.labelGroup}`
      try {
        const savedState = sessionStorage.getItem(stateKey)
        return savedState !== null && savedState !== undefined
      } catch (error) {
        return false
      }
    },
    // 从 sessionStorage 恢复筛选条件
    restoreFilterValues() {
      const stateKey = `filterContainer_state_${this.labelGroup}`
      try {
        const savedValues = sessionStorage.getItem(stateKey)
        if (savedValues) {
          const values = JSON.parse(savedValues)
          // 如果 filters 已加载，合并保存的值
          if (this.filters.length > 0) {
            const mergedValues = {}
            this.filters.forEach(filter => {
              if (values[filter.field_name] !== undefined) {
                mergedValues[filter.field_name] = values[filter.field_name]
              } else {
                // 如果没有保存的值，使用默认值
                if (filter.search_type === 'option') {
                  const defaultValue = filter.default_value || ''
                  mergedValues[filter.field_name] = defaultValue ? [defaultValue] : []
                } else if (filter.search_type === 'radio') {
                  // 单选类型，默认值应该是字符串或空字符串
                  mergedValues[filter.field_name] = filter.default_value || ''
                } else {
                  mergedValues[filter.field_name] = filter.default_value || ''
                }
              }
            })
            this.filterValues = mergedValues
          } else {
            // 如果 filters 还没加载，先保存 values，等 filters 加载后再使用
            this.filterValues = values
          }
        }
      } catch (error) {
        console.error('恢复筛选条件失败:', error)
      }
    },
    handleToggleMore() {
      this.isMoreExpanded = !this.isMoreExpanded
    },
    // 暴露给父组件调用的方法
    toggleExpand() {
      this.isMoreExpanded = !this.isMoreExpanded
    },
    getShowMoreCount(filter) {
      // 企业字段显示更多按钮
      if (filter.field_name === 'enterprise') {
        return 12
      }
      // 疾病领域字段（器械注册查询）显示更多按钮
      if (filter.field_name === 'disease' && this.labelGroup === 5) {
        return 12
      }
      return 10
    },
    checkMobile() {
      this.isMobile = isMobileDevice()
    }
  }
}
</script>

<style scoped>
.filter-input {
  width: 100%;
}

@media (max-width: 767px) {
  .filter-container {
    padding: 0;
    margin: 0;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
  }
}
</style>

